/**
 * RadioGroup Component (shadcn/ui)
 *
 * Componente RadioGroup acessível baseado em @radix-ui/react-radio-group.
 * Suporta opções vertical/horizontal, disabled, dark mode e react-hook-form.
 *
 * @module components/ui
 * @since 2026-04-06
 *
 * @example
 * // Uso básico com options
 * <RadioGroup
 *   label="Gênero"
 *   options={[
 *     { value: 'M', label: 'Masculino' },
 *     { value: 'F', label: 'Feminino' },
 *   ]}
 *   value={gender}
 *   onChange={setGender}
 * />
 *
 * @example
 * // Layout horizontal
 * <RadioGroup
 *   options={options}
 *   value={value}
 *   onChange={onChange}
 *   layout="horizontal"
 * />
 *
 * @example
 * // Com react-hook-form
 * <RadioGroup
 *   {...register('status')}
 *   options={statusOptions}
 *   value={watch('status')}
 *   onChange={(val) => setValue('status', val)}
 *   error={errors.status?.message}
 * />
 */

import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Circle } from 'lucide-react';
import { cn } from '@/lib/utils';

// ============================================
// TYPES
// ============================================

export interface RadioOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface RadioGroupProps {
  /** Nome do grupo (para react-hook-form e acessibilidade) */
  name?: string;
  /** Valor selecionado */
  value?: string | number | null;
  /** Callback quando o valor muda */
  onChange?: (value: string | number) => void;
  /** Lista de opções */
  options?: RadioOption[];
  /** Label do grupo */
  label?: string;
  /** Mensagem de erro */
  error?: string;
  /** Texto de ajuda */
  helperText?: string;
  /** Desabilitar todo o grupo */
  disabled?: boolean;
  /** Layout das opções */
  layout?: 'vertical' | 'horizontal';
  /** Classes CSS adicionais */
  className?: string;
}

// ============================================
// RADIO GROUP ROOT
// ============================================

const RadioGroupRoot = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Root
    ref={ref}
    className={cn('grid gap-2', className)}
    {...props}
  />
));
RadioGroupRoot.displayName = RadioGroupPrimitive.Root.displayName;

// ============================================
// RADIO GROUP ITEM
// ============================================

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    className={cn(
      'aspect-square h-4 w-4 rounded-full border border-primary text-primary shadow',
      'focus:outline-none focus-visible:ring-1 focus-visible:ring-ring',
      'disabled:cursor-not-allowed disabled:opacity-50',
      className
    )}
    {...props}
  >
    <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
      <Circle className="h-3.5 w-3.5 fill-primary" />
    </RadioGroupPrimitive.Indicator>
  </RadioGroupPrimitive.Item>
));
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

// ============================================
// RADIO GROUP (HIGH-LEVEL COMPONENT)
// ============================================

/**
 * Componente RadioGroup de alto nível com suporte a options, label, erro e layout.
 * Compatível com a API anterior (name, value, onChange, options, label, error, disabled).
 */
export function RadioGroup({
  name,
  value,
  onChange,
  options = [],
  label,
  error,
  helperText,
  disabled = false,
  layout = 'vertical',
  className = '',
}: RadioGroupProps) {
  const groupName = name || `radio-${Math.random().toString(36).substr(2, 9)}`;

  const handleValueChange = (newValue: string) => {
    if (!onChange) return;

    // Tentar converter para número se a opção original for número
    const matchingOption = options.find((opt) => String(opt.value) === newValue);
    if (matchingOption !== undefined) {
      onChange(matchingOption.value);
    } else {
      onChange(newValue);
    }
  };

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-2">
          {label}
        </label>
      )}

      <RadioGroupRoot
        name={groupName}
        value={value !== null && value !== undefined ? String(value) : undefined}
        onValueChange={handleValueChange}
        disabled={disabled}
        className={cn(
          layout === 'horizontal' ? 'flex flex-row flex-wrap gap-4' : 'flex flex-col gap-2'
        )}
      >
        {options.map((option) => {
          const itemId = `${groupName}-${option.value}`;
          const isDisabled = disabled || option.disabled;

          return (
            <div key={option.value} className="flex items-center gap-2">
              <RadioGroupItem
                id={itemId}
                value={String(option.value)}
                disabled={isDisabled}
              />
              <label
                htmlFor={itemId}
                className={cn(
                  'text-sm text-foreground',
                  isDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                )}
              >
                {option.label}
              </label>
            </div>
          );
        })}
      </RadioGroupRoot>

      {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
      {helperText && !error && (
        <p className="mt-1 text-sm text-muted-foreground">{helperText}</p>
      )}
    </div>
  );
}

// ============================================
// EXPORTS PRIMITIVOS (para uso avançado)
// ============================================

export { RadioGroupRoot, RadioGroupItem };
